import { FrontendDiagnosticsService, getSparkClientContext, SPARK_DIAGNOSTIC_REQUEST } from './services/frontend-diagnostics.service';
import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse, HttpErrorResponse, HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Router } from "@angular/router";
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AlertService } from '../shared/services/alert.service';
import { Utils } from './utils';
import { AuthService, isRefreshSuperseded } from '../auth';
import { JwTokenModel } from './api/models';
import { MiniProfilerService } from './services/mini-profiler.service';
import { AcademyModeService, SparkMode } from './services/academy-mode.service';
import { ParentAuthService } from '../parent/parent-auth.service';


@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  timer: any;
  private expiredTokenRefreshInProgress = false;
  constructor(private router: Router,

    private alertService: AlertService,

    private authService: AuthService,
    private http: HttpClient,
    private profiler: MiniProfilerService,
    private academyModeService: AcademyModeService,
    private parentAuthService: ParentAuthService,
    private diagnostics: FrontendDiagnosticsService

  ) { }
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // add authorization header with jwt token if available
    // Use path check so API requests are detected for both relative ('/api/...') and absolute (e.g. 'https://host/api/...') URLs (QA/prod often use empty webApiRoot or different base URLs)
    var isAPIRequest = request.url.includes('/api/');
    const accountPath = request.url.split('?')[0];
    const isTokenRequest = /\/api\/Account\/(Authenticate|RefreshToken)$/.test(accountPath);
    // Me stays cacheable for offline startup. Only uncached account calls calibrate the clock.
    const isClockRequest = isTokenRequest || accountPath.endsWith('/api/Account/Test');

    if (isAPIRequest) {
      const client = getSparkClientContext();
      const modeId = this.academyModeService.getCurrentSparkModeId();
      let params = request.params.set('sparkMode', String(modeId));
      if (modeId === SparkMode.Academy) {
        params = params.set('academyMode', '1');
      } else {
        params = params.delete('academyMode');
      }
      request = request.clone({ params });

      //get the token from the local storage
      const token = localStorage.getItem('tokenInfo');
      if (token) {
        const newTokenInfo = JSON.parse(token) as JwTokenModel;
        // Adopt a later credential only for this user and impersonation context.
        if (this.authService.sameSession(this.authService.tokenInfo?.access_token, newTokenInfo.access_token)
          && newTokenInfo.expires_at && new Date(newTokenInfo.expires_at) > new Date(this.authService.tokenInfo?.expires_at || 0)) {
          this.authService.tokenInfo = newTokenInfo;
          this.authService.tokenInfo$.next(newTokenInfo);
        }
      }
      const isParentRequest = request.url.includes('/api/Parent/');
      request = request.clone({
        setHeaders: {
          Authorization: isParentRequest
            ? (this.parentAuthService.isSignedIn() ? `Bearer ${this.parentAuthService.tokenInfo?.access_token}` : '')
            : (this.authService.isSignedIn() ? `Bearer ${this.authService.tokenInfo?.access_token}` : ''),
          'X-Spark-Client': client.source,
          'X-Spark-Browser': client.browser,
          'X-Spark-Platform': client.platform,
          'X-Spark-Entry-Id': this.diagnostics.entryId,
          'X-Spark-Entry-Source': this.diagnostics.entrySource,
          clientname: this.authService.getClientFromUrl()
          //'x-api-version': `${environment.apiServer.apiVersion}`
        }
      });      
      if (isClockRequest) {
        // Angular's API cache can return old headers on timeout/offline fallback.
        request = request.clone({ setHeaders: { 'ngsw-bypass': 'true' } });
      }
    }

    if (request.context.get(SPARK_DIAGNOSTIC_REQUEST)) {
      return next.handle(request);
    }

    // Also handle errors globally
    return next.handle(request).pipe(tap({
      next: (event: HttpEvent<any>) => {

        if (event instanceof HttpErrorResponse) {

        }
        if (event instanceof HttpResponse) {
          if (isAPIRequest && isClockRequest) {
            this.authService.observeServerTime(event.headers.get('X-Spark-Server-Utc') || event.headers.get('Date'));
          }
          if (isAPIRequest && !isClockRequest && this.authService.isSignedIn() && this.authService.accessTokenRemainingMs() === null) {
            this.authService.calibrateServerClock();
          }

          // do stuff with response if you want


          if (isAPIRequest
            && !isTokenRequest
            && this.authService.isSignedIn()
            && this.authService.accessTokenRemainingMs() !== null
            && this.authService.accessTokenRemainingMs()! < 30 * 60 * 1000
            && this.authService.hasUsableRefreshToken()) {
            // If we are close to the token expiration, refresh it
            this.authService.refreshTokenOnce().subscribe({ error: () => { } });
          }

          this.timerAutoLogout();

          if (!environment.production) {
            // https://github.com/MiniProfiler/dotnet/issues/502
            
            try {
              const miniProfilerIds = event.headers.get('X-MiniProfiler-Ids');
              if (miniProfilerIds) {
                this.profiler.fetch(JSON.parse(miniProfilerIds));
              }
            } catch (err) { }
          }

        }
      },
      error: err => {
        if (isAPIRequest && isClockRequest) {
          this.authService.observeServerTime(err.headers?.get('X-Spark-Server-Utc') || err.headers?.get('Date'));
        }
        if (isAPIRequest && !isClockRequest && this.authService.isSignedIn() && this.authService.accessTokenRemainingMs() === null) {
          this.authService.calibrateServerClock();
        }
        // Handle this err
        console.error(err, request.url);

      }
    }));

  }

  timerAutoLogout() {
    if (this.timer) {
      clearTimeout(this.timer);
    }

    this.timer = setTimeout(() => {

      let expires = this.authService.tokenInfo?.expires_at;
      if (!expires) {
        return;
      }
      const remTime = this.authService.accessTokenRemainingMs();
      // Wait for an API response after reload; never substitute the device clock.
      if (remTime === null) {
        this.timerAutoLogout();
        return;
      }
      
      if (!isFinite(remTime) || remTime <= 0) {
        this.refreshExpiredTokenOrSignOut();
        return;
      }
      if (remTime < 300000) { //5 minutes left and not already showing confirm (need to add not already showing)
        this.authService.showSessionConfirm();
      }

      this.timerAutoLogout();
    }, 10000)
    
  }

  private refreshExpiredTokenOrSignOut() {
    this.authService.hideSessionConfirm();

    if (this.expiredTokenRefreshInProgress) {
      return;
    }

    if (!this.authService.hasUsableRefreshToken()) {
      this.signOutExpiredSession();
      return;
    }

    this.expiredTokenRefreshInProgress = true;
    this.authService.refreshTokenOnce().subscribe({
      next: token => {
        this.expiredTokenRefreshInProgress = false;
        if (token?.access_token) {
          this.timerAutoLogout();
          return;
        }

        this.signOutExpiredSession();
      },
      error: (error) => {
        this.expiredTokenRefreshInProgress = false;
        if (isRefreshSuperseded(error)) {
          this.timerAutoLogout();
          return;
        }
        this.signOutExpiredSession();
      }
    });
  }

  private signOutExpiredSession() {
    this.authService.hideSessionConfirm();
    this.authService.signOut().subscribe();
  }
}
